"use client";

import { useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { HeroBackground } from "@/components/home/hero/HeroBackground";
import { HeroCTAButtons } from "@/components/home/hero/HeroCTAButtons";
import { ProgramStatsBar } from "@/components/programs/ProgramStatsBar";
import { EASE_SMOOTH } from "@/components/motion/constants";
import type { AboutHeroContent } from "@/lib/api-types";

interface AboutPageHeroProps {
  hero: AboutHeroContent;
}

export function AboutPageHero({ hero }: AboutPageHeroProps) {
  const reduced = useReducedMotion();
  const backgroundRef = useRef<HTMLDivElement>(null);

  return (
    <section className="relative mt-16 flex min-h-[min(85vh,720px)] flex-col justify-end overflow-hidden">
      <HeroBackground
        ref={backgroundRef}
        src={hero.imageSrc}
        alt={hero.imageAlt}
        kenBurns={!reduced}
        objectPosition="center"
        overlay="soft"
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-12 pt-28 md:px-6 md:pb-16">
        <div className="max-w-2xl">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--charity-gold)] drop-shadow-sm">
            {hero.eyebrow}
          </p>
          <h1 className="font-display text-4xl font-bold leading-tight text-white drop-shadow md:text-5xl lg:text-6xl">
            {hero.title}{" "}
            <span className="text-[var(--charity-gold)]">{hero.accentWord}</span>
          </h1>
          <motion.p
            className="mt-4 max-w-xl text-sm leading-relaxed text-white/90 drop-shadow sm:text-base"
            initial={reduced ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE_SMOOTH, delay: 0.15 }}
          >
            {hero.subtitle}
          </motion.p>
          <div className="mt-6">
            <HeroCTAButtons
              primary={hero.primaryCta}
              secondary={hero.secondaryCta}
            />
          </div>
        </div>

        <div className="mt-10 md:mt-12">
          <ProgramStatsBar stats={hero.stats} />
        </div>
      </div>
    </section>
  );
}
